// Convert a monetary amount into its English words representation, the way it
// appears on cheques and receipts (e.g. "Two thousand five hundred Ghana cedis
// and fifty pesewas only"). The integer part becomes the major unit; the two
// fractional decimals become the minor unit (100 minor = 1 major).

export interface AmountInWordsOptions {
  /** ISO-ish currency code (e.g. "GHS", "USD"). Used to look up unit names. */
  currencyCode?: string;
  majorSingular?: string;
  majorPlural?: string;
  minorSingular?: string;
  minorPlural?: string;
}

interface CurrencyUnits {
  majorSingular: string;
  majorPlural: string;
  minorSingular: string;
  minorPlural: string;
}

// Common currency -> unit name mapping. Unknown codes fall back to a generic
// "<CODE>" major unit and "cent(s)" minor unit.
const CURRENCY_UNITS: Record<string, CurrencyUnits> = {
  GHS: {
    majorSingular: "Ghana cedi",
    majorPlural: "Ghana cedis",
    minorSingular: "pesewa",
    minorPlural: "pesewas",
  },
  USD: {
    majorSingular: "dollar",
    majorPlural: "dollars",
    minorSingular: "cent",
    minorPlural: "cents",
  },
  EUR: {
    majorSingular: "euro",
    majorPlural: "euros",
    minorSingular: "cent",
    minorPlural: "cents",
  },
  GBP: {
    majorSingular: "pound",
    majorPlural: "pounds",
    minorSingular: "penny",
    minorPlural: "pence",
  },
  NGN: {
    majorSingular: "naira",
    majorPlural: "naira",
    minorSingular: "kobo",
    minorPlural: "kobo",
  },
};

// Default to Ghana cedis/pesewas when no currency code is supplied.
const DEFAULT_UNITS: CurrencyUnits = CURRENCY_UNITS.GHS;

const ONES = [
  "zero",
  "one",
  "two",
  "three",
  "four",
  "five",
  "six",
  "seven",
  "eight",
  "nine",
  "ten",
  "eleven",
  "twelve",
  "thirteen",
  "fourteen",
  "fifteen",
  "sixteen",
  "seventeen",
  "eighteen",
  "nineteen",
];

const TENS = [
  "",
  "",
  "twenty",
  "thirty",
  "forty",
  "fifty",
  "sixty",
  "seventy",
  "eighty",
  "ninety",
];

// Scale words for groups of three digits, from least to most significant.
const SCALES = ["", "thousand", "million", "billion", "trillion"];

// Words for a number in [0, 999].
function threeDigitsToWords(n: number): string {
  const parts: string[] = [];
  const hundreds = Math.floor(n / 100);
  const remainder = n % 100;

  if (hundreds > 0) {
    parts.push(`${ONES[hundreds]} hundred`);
  }

  if (remainder > 0) {
    if (remainder < 20) {
      parts.push(ONES[remainder]);
    } else {
      const tens = Math.floor(remainder / 10);
      const ones = remainder % 10;
      parts.push(ones > 0 ? `${TENS[tens]} ${ONES[ones]}` : TENS[tens]);
    }
  }

  return parts.join(" ");
}

// Words for a non-negative integer (supports up to the largest defined scale).
function integerToWords(value: number): string {
  if (value === 0) return ONES[0];

  // Break the number into groups of three digits, least significant first.
  const groups: number[] = [];
  let remaining = value;
  while (remaining > 0) {
    groups.push(remaining % 1000);
    remaining = Math.floor(remaining / 1000);
  }

  const segments: string[] = [];
  for (let i = groups.length - 1; i >= 0; i--) {
    const group = groups[i];
    if (group === 0) continue;
    const words = threeDigitsToWords(group);
    const scale = SCALES[i] || "";
    segments.push(scale ? `${words} ${scale}` : words);
  }

  return segments.join(" ");
}

function capitalizeFirst(text: string): string {
  if (!text) return text;
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Convert `amount` to a receipt/cheque style words sentence, e.g.
 * `amountInWords(2500.5)` -> "Two thousand five hundred Ghana cedis and fifty pesewas only".
 *
 * - Defaults to Ghana cedis/pesewas when no currency code is given.
 * - Non-finite/NaN input is treated as 0.
 * - Negative amounts are prefixed with "Minus ".
 * - Always ends with "only". The minor-unit clause is omitted when there are
 *   no minor units.
 */
export function amountInWords(
  amount: number,
  opts: AmountInWordsOptions = {},
): string {
  const code = opts.currencyCode
    ? opts.currencyCode.trim().toUpperCase()
    : undefined;

  const base: CurrencyUnits = code
    ? CURRENCY_UNITS[code] || {
        majorSingular: code,
        majorPlural: code,
        minorSingular: "cent",
        minorPlural: "cents",
      }
    : DEFAULT_UNITS;

  const units: CurrencyUnits = {
    majorSingular: opts.majorSingular ?? base.majorSingular,
    majorPlural: opts.majorPlural ?? base.majorPlural,
    minorSingular: opts.minorSingular ?? base.minorSingular,
    minorPlural: opts.minorPlural ?? base.minorPlural,
  };

  const safeAmount = Number.isFinite(amount) ? amount : 0;
  const negative = safeAmount < 0;
  const absolute = Math.abs(safeAmount);

  // Round to 2 decimals via integer minor units to avoid float artifacts
  // (e.g. 0.005). 250050 minor units = 2500 major + 50 minor.
  const totalMinor = Math.round(absolute * 100);
  const majorValue = Math.floor(totalMinor / 100);
  const minorValue = totalMinor % 100;

  const majorWords = integerToWords(majorValue);
  const majorUnit =
    majorValue === 1 ? units.majorSingular : units.majorPlural;

  let sentence = `${majorWords} ${majorUnit}`;

  if (minorValue > 0) {
    const minorWords = integerToWords(minorValue);
    const minorUnit =
      minorValue === 1 ? units.minorSingular : units.minorPlural;
    sentence += ` and ${minorWords} ${minorUnit}`;
  }

  sentence += " only";

  if (negative) {
    sentence = `minus ${sentence}`;
  }

  return capitalizeFirst(sentence);
}
